import React from 'react';
import { Grid } from '@material-ui/core';
import {
  Button,
  TextField,
  createAutocompleteAddressField,
} from '@roc/ui';
import { observer } from 'mobx-react-lite';
import TitleAgentV2Store from '../../../stores/documentForms/titleAgentV2/titleAgentV2Store';
import { TitleContactSection } from './titleContactSection';

interface TitleCompanyCreateFormProps {
  store: TitleAgentV2Store;
  disabled?: boolean;
}

const TitleCompanyCreateForm = observer(
  ({ store, disabled }: TitleCompanyCreateFormProps) => {
    const formStore = store.titleCompanyFormStore;
    const company = store.pendingExistingCompany;

    const handleAddressChange = event => {
      const { value, fullInfo } = event?.target || {};
      const { address_components, geometry } = fullInfo || {};
      formStore.saveAddressField(value, address_components, geometry);
    };

    const street = [company?.streetNumber, company?.streetName]
      .filter(Boolean)
      .join(' ') || company?.address || '';

    return (
      <Grid item xs={12}>
        <Grid container spacing={2}>

          {/* Address: read-only for existing company unless accepted (where user can edit) */}
          {store.isExistingCompanySelected && !store.isAccepted ? (
            <>
              <Grid item xs={12}>
                <TextField
                  standaloneLabel
                  label="Street number and name"
                  variant="outlined"
                  value={street}
                  fullWidth
                  testId="existingTitleStreet"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  standaloneLabel
                  label="City"
                  variant="outlined"
                  value={company?.city ?? ''}
                  fullWidth
                  testId="existingTitleCity"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  standaloneLabel
                  label="State"
                  variant="outlined"
                  value={company?.state ?? ''}
                  fullWidth
                  testId="existingTitleState"
                  disabled
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  standaloneLabel
                  label="Zip code"
                  variant="outlined"
                  value={company?.zipCode ?? ''}
                  fullWidth
                  testId="existingTitleZip"
                  disabled
                />
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              {createAutocompleteAddressField({
                store: formStore,
                testId: 'newTitleCompanyAddress',
                fieldName: 'address',
                label: 'Company address',
                disabled,
                onChange: handleAddressChange,
              })}
            </Grid>
          )}

          {/* Shared contact section */}
          <Grid item xs={12}>
            <TitleContactSection
              contacts={store.contacts}
              selectedContactIds={store.selectedContactIds}
              onToggle={store.toggleContact.bind(store)}
              onAddContact={draft => store.addAndSelectNewContact(draft)}
              onUpdateContact={updated => store.updateContact(updated.contactId!, updated)}
              disableEditForPersisted={store.isAccepted}
              disabled={disabled}
            />
          </Grid>

          {/* Contacted date: system-populated once the title chat room is first created, so this
              only ever has a value on an edit (never on initial create) and must stay read-only. */}
          {store.isAccepted && store.loanStore.loanDetails?.loanClosingData?.titleContactedDate && (
            <Grid item xs={12} sm={6}>
              <TextField
                standaloneLabel
                label="Contacted Date"
                type="text"
                variant="outlined"
                value={store.loanStore.loanDetails?.loanClosingData?.titleContactedDate}
                fullWidth
                testId="titleContactedDate"
                disabled
              />
            </Grid>
          )}

        </Grid>
      </Grid>
    );
  }
);

export default TitleCompanyCreateForm;
